/**
 * The hall's quiet life and comfort: light particles drifting up through the air (one
 * points draw call, all motion in the vertex shader) and two lounges by the windows, each
 * three pods around a small glowing table (five instanced draw calls for both).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TOKENS } from '../../ui/theme.ts';
import { glowMaterial, radialTexture, seeded, type TimeUniform } from './glow.ts';

export function buildParticles(time: TimeUniform, radius: number, height: number): THREE.Points {
  const rand = seeded(404);
  const count = 700;
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count * 2);
  for (let i = 0; i < count; i++) {
    const a = rand() * Math.PI * 2;
    const r = Math.sqrt(rand()) * (radius - 1.5);
    positions.set([Math.cos(a) * r, rand() * height, Math.sin(a) * r], i * 3);
    seeds.set([rand(), rand()], i * 2);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 2));
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, height / 2, 0), radius + height);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: time,
      uHeight: { value: height },
      uMap: { value: radialTexture() },
      uColour: { value: new THREE.Color(TOKENS.cyanSoft) },
    },
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform float uHeight;
      attribute vec2 aSeed;
      varying float vAlpha;
      void main() {
        vec3 p = position;
        float speed = 0.05 + aSeed.x * 0.12;
        p.y = mod(p.y + uTime * speed, uHeight);
        p.x += sin(uTime * 0.13 + aSeed.y * 40.0) * 0.6;
        p.z += cos(uTime * 0.11 + aSeed.x * 40.0) * 0.6;
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        float edge = smoothstep(0.0, 0.08, p.y / uHeight) * (1.0 - smoothstep(0.7, 1.0, p.y / uHeight));
        vAlpha = edge * (0.35 + 0.65 * (0.5 + 0.5 * sin(uTime * (0.6 + aSeed.y) + aSeed.x * 30.0)));
        gl_PointSize = (0.035 + aSeed.y * 0.05) * 700.0 / -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D uMap;
      uniform vec3 uColour;
      varying float vAlpha;
      void main() {
        float a = texture2D(uMap, gl_PointCoord).a * vAlpha * 0.8;
        gl_FragColor = vec4(uColour * a, a);
      }`,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const points = new THREE.Points(geometry, material);
  points.name = 'lobby-particles';
  points.renderOrder = 8;
  return points;
}

/** Lounge centres: east and west, beside the windows. */
export const LOUNGES = [
  { x: 12, z: 1 },
  { x: -12, z: 1 },
];

export function buildLounges(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'lounges';
  // An egg chair: a white shell, open toward the table from the seat up, on a slim pedestal;
  // a charcoal seat and back cushion inside. Shell and pedestal share one geometry and
  // material, seat and cushion another, so the six pods are two draw calls.
  const shell: [number, number][] = [
    [0.1, 0.34],
    [0.32, 0.36],
    [0.48, 0.44],
    [0.57, 0.6],
    [0.6, 0.8],
    [0.58, 1.0],
    [0.52, 1.18],
    [0.42, 1.33],
    [0.28, 1.44],
    [0.1, 1.5],
  ];
  const open = 2.2; // radians left open at the front
  const shellGeometry = mergeGeometries([
    new THREE.LatheGeometry(
      shell.map(([r, y]) => new THREE.Vector2(r, y)),
      48,
      open / 2,
      Math.PI * 2 - open,
    ),
    new THREE.CylinderGeometry(0.1, 0.3, 0.34, 32).translate(0, 0.17, 0),
  ])!;
  const cushionGeometry = mergeGeometries([
    new THREE.LatheGeometry(
      shell.slice(2, 9).map(([r, y]) => new THREE.Vector2(Math.max(0.05, r - 0.08), y + 0.02)),
      40,
      open / 2 + 0.2,
      Math.PI * 2 - open - 0.4,
    ),
    new THREE.CylinderGeometry(0.44, 0.42, 0.12, 40).translate(0, 0.5, 0.04),
  ])!;
  // Phong, not a physically based material: a glossy highlight is all the shell needs, and
  // the lounges sit in view of the reception (fill rate is the Quest's limit).
  const shellMaterial = new THREE.MeshPhongMaterial({
    color: TOKENS.mist,
    specular: '#9fb4c8',
    shininess: 70,
    side: THREE.DoubleSide,
  });
  const cushionMaterial = new THREE.MeshLambertMaterial({
    color: '#1d2836',
    side: THREE.DoubleSide,
  });
  const pods: { x: number; z: number; yaw: number }[] = [];
  for (const l of LOUNGES)
    for (let k = 0; k < 3; k++) {
      // Three pods round the table, the gap toward the hall's centre left open.
      const a = (k / 3) * Math.PI * 2 + (l.x > 0 ? -Math.PI / 2 : Math.PI / 2) + Math.PI / 3;
      const x = l.x + Math.sin(a) * 1.6;
      const z = l.z + Math.cos(a) * 1.6;
      // The open side (lathe angle 0 = +z) faces the table.
      pods.push({ x, z, yaw: Math.atan2(l.x - x, l.z - z) });
    }
  const shells = new THREE.InstancedMesh(shellGeometry, shellMaterial, pods.length);
  const cushions = new THREE.InstancedMesh(cushionGeometry, cushionMaterial, pods.length);
  const d = new THREE.Object3D();
  pods.forEach((p, i) => {
    d.position.set(p.x, 0, p.z);
    d.rotation.set(0, p.yaw, 0);
    d.updateMatrix();
    shells.setMatrixAt(i, d.matrix);
    cushions.setMatrixAt(i, d.matrix);
  });
  group.add(shells, cushions);
  // Tables: a dark floating disc with a ring of light round its edge.
  const tableTop = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.5, 0.46, 0.05, 48).translate(0, 0.52, 0),
    new THREE.MeshPhongMaterial({ color: '#0e131a', specular: '#4a6a86', shininess: 90 }),
    LOUNGES.length,
  );
  const tableGlow = new THREE.InstancedMesh(
    new THREE.TorusGeometry(0.5, 0.012, 6, 64).rotateX(Math.PI / 2).translate(0, 0.545, 0),
    glowMaterial(TOKENS.cyan, 1),
    LOUNGES.length,
  );
  LOUNGES.forEach((l, i) => {
    d.position.set(l.x, 0, l.z);
    d.rotation.set(0, 0, 0);
    d.updateMatrix();
    tableTop.setMatrixAt(i, d.matrix);
    tableGlow.setMatrixAt(i, d.matrix);
  });
  group.add(tableTop, tableGlow);
  return group;
}
